import React, { useEffect } from "react";
import { Link } from "gatsby";

import HeaderComponent, {
	headerTransform,
} from "./../../../../components/header";
import FooterComponent, {
	footerTransform,
} from "./../../../../components/footer";
import Banner, {
	bannerTransform,
} from "./../../../../components/university/Banner";

import { isAddMargin, setCustomPadding } from "../../helpers";
import {
	addToDataLayer,
	getCookie,
	getEssentialData,
	hbsptEventHandler,
} from "@website-builder/utilities-index";
import { triggerVWOSegmentEvents } from "@website-builder/utilities-index";
import Sections, { sectionTransform } from "./../../../sections";
import Components, { ComponentTransform } from "./../../../../components";

import {
	UniversityWrapper,
	LeftComponentWrapper,
	RightComponentWrapper,
} from "./style";
import { QueryClientProvider } from "react-query";
import { queryClient } from "@website-builder/utilities-index";
import { B2U_REBRAND_PHASED_ROLLOUT_VWO_COOKIE } from "@website-builder/ui/b2u";

const UniversityLayout = ({
	story,
	basicConfig = {},
	token = {},
	storyList = undefined,
	globalVariableWithURL = [],
	mentorProfilesData = [],
}) => {
	// You can add data that are set as page properties .This will be passed as context to all the components
	let context = getEssentialData(story);
	let header = null;
	let footer = null;
	let rightSection = [];
	let leftSection = [];
	let banner = null;
	let isRebrandPage = false;
	// VWO's traffic split cookie used for phased rollout of UP Rebrand
	let isRebrandFlow = false;

	if (token) {
		context.token = token;
	}

	useEffect(() => {
		isRebrandFlow = getCookie(B2U_REBRAND_PHASED_ROLLOUT_VWO_COOKIE) === "2";

		const eventListenerCallback = (event) => {
			hbsptEventHandler(event, {
				is_rebrand_page: isRebrandPage,
				is_rebrand_flow: isRebrandFlow,
				workshop_id: story?.content?.workshopId,
				triggerHubspotCustomEvent: story?.content?.triggerHubspotCustomEvent,
			});
		};

		window.addEventListener("message", eventListenerCallback);
		triggerVWOSegmentEvents();
		pushAnalyticsData();

		return () => {
			window.removeEventListener("message", eventListenerCallback);
		};
	});

	const pushAnalyticsData = () => {
		addToDataLayer({
			page_workshop_id: story.content.workshopId,
			page_full_slug: story.full_slug,
			is_rebrand_page: isRebrandPage,
			is_rebrand_flow: isRebrandFlow,
		});
	};

	if (token) {
		context.token = token;
	}

	try {
		//  For stories that has only navigation(header and footer) we need to get only header and footer
		if (
			story.content.component &&
			story.content.component.toLowerCase().indexOf("layout") !== -1
		) {
			header = headerTransform(
				story.content.header[0],
				globalVariableWithURL,
				story.content?.stickyHeader?.[0],
			);
			footer = footerTransform(story.content.footer[0]);
		} else {
			header = headerTransform(
				story.content.layout.content.header[0],
				globalVariableWithURL,
				story.content?.stickyHeader?.[0],
			);
			footer = footerTransform(story.content.layout.content.footer[0]);
			leftSection = story.content.leftSection;
			rightSection = story.content.rightSection;
			story?.content?.banner[0] &&
				(banner = bannerTransform(story?.content?.banner[0], { basicConfig }));
		}
	} catch (error) {
		console.error(error);
	}

	/* Loads sections(like fullwidth section) here*/
	const constructSection = (sections, ComponentWrapper, parentSection) => {
		return sections.map(
			({ component: sectionName, ...sectionComponents }, index) => {
				const Section = Sections[sectionName];
				let sectionProps = {};
				if (sectionTransform[sectionName]) {
					sectionProps = sectionTransform[sectionName](sectionComponents);
				}
				const name = sectionComponents?.components?.[0]?.component || "";
				if (Section) {
					return (
						<Section
							key={`${sectionName}-${index}`}
							context={context}
							className={isAddMargin(name, parentSection) ? "add-margin" : ""}
							{...sectionProps}
						>
							{constructComponents(
								sectionComponents,
								ComponentWrapper,
								parentSection,
							)}
						</Section>
					);
				} else {
					console.error(`${sectionName} is not difined in the Blocks`);
					new Error(`${sectionName} is not difined in the Blocks `);
				}
			},
		);
	};

	/* Loads components(can be anything that comes inside component */
	const constructComponents = (
		{ components },
		ComponentWrapper,
		parentSection,
	) => {
		return components.map(({ component: name, ...props }, index) => {
			const Component = Components[name];
			let componentProps = {};
			if (ComponentTransform[name]) {
				componentProps = ComponentTransform[name](props, {
					story: story.content,
					storyList,
					context,
					basicConfig,
					globalVariableWithURL,
					mentorProfilesData,
				});
			} else {
				componentProps = props;
			}
			if (Component) {
				return (
					// addPadding will change when actual implementation happens
					<ComponentWrapper
						hide={componentProps.hideElementInSmallScreen}
						addPadding={checkPadding(name)}
						addMargin={isAddMargin(name, parentSection)}
						padding={setCustomPadding(name, parentSection)}
						key={`${name}-${index}`}
					>
						<Component
							context={context}
							parentSection={parentSection}
							{...componentProps}
						></Component>
					</ComponentWrapper>
				);
			} else {
				console.error(`${name} is not difined in the Components`);
				new Error(`${name} is not difined in the Components `);
			}
		});
	};

	const checkPadding = (Component) => {
		if (
			Component === "Accordion" ||
			Component === "UpcomingPrograms" ||
			Component === "QuickLinksPanel" ||
			Component === "Tuition" ||
			Component === "FormWrapper"
		)
			return false;
		else return true;
	};

	return (
		<QueryClientProvider client={queryClient}>
			<UniversityWrapper>
				<HeaderComponent
					Link={Link}
					{...header}
					context={context}
					basicConfig={basicConfig}
				></HeaderComponent>
				<main className="main-wrapper">
					<section className="banner">
						<Banner
							{...banner}
							context={context}
							basicConfig={basicConfig}
						></Banner>
					</section>
					<div className="section-container">
						<section className="left-section">
							{constructSection(leftSection, LeftComponentWrapper, "left")}
						</section>
						<section className="right-section">
							{constructSection(rightSection, RightComponentWrapper, "right")}
						</section>
					</div>
				</main>
				<FooterComponent
					Link={Link}
					{...footer}
					context={context}
					basicConfig={basicConfig}
				/>
			</UniversityWrapper>
		</QueryClientProvider>
	);
};

export default UniversityLayout;
